import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { TableColumnSettingsProvider } from "@providers/TableColumnSettingsProvider/TableColumnSettingsProvider";

import {
  ClientEmployeeFilterForm,
  ClientEmployeeFilterFormRef,
  ClientEmployeeFilterFormValues,
} from "@components/Forms/ClientEmployeeFilterForm/ClientEmployeeFilterForm";
import { ClientEmployeesTable } from "@components/Tables";
import {
  ClientEmployeesTableConfig,
  areFiltersActive,
  getDefaultFilterValues,
} from "@components/Tables/ClientEmployeesTable/ClientEmployeesTableConfig";
import { ClientEmployeesTableColumnCustomization } from "@components/Tables/ClientEmployeesTable/UI/ClientEmployeesTableColumnCustomization";
import {
  DEFAULT_CLIENT_EMPLOYEE_TABLE_COLUMN_ORDER,
  DEFAULT_CLIENT_EMPLOYEE_TABLE_COLUMN_SETTINGS,
} from "@components/Tables/ClientEmployeesTable/UI/ClientEmployeesTableColumns";
import { useClientEmployeesTableExport } from "@components/Tables/ClientEmployeesTable/utils/useClientEmployeesTableExport";

import { Box } from "@design-system/Box/Box";
import { Card } from "@design-system/Card/Card";
import { IconButton } from "@design-system/IconButton/IconButton";
import { SlideInPanel } from "@design-system/SlideInPanel";
import { Text } from "@design-system/Text/Text";

type Props = {
  clientId: string;
  initialPageIndex?: number;
  showFooter?: boolean;
  showHeader?: boolean;
  showPageSizeDropdown?: boolean;
  pageSize?: number;
};

export const ClientEmployeesCard = ({
  clientId,
  initialPageIndex = 0,
  showFooter = true,
  showHeader = true,
  showPageSizeDropdown = true,
  pageSize = 30,
}: Props) => {
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [isColumnSettingsOpen, setIsColumnSettingsOpen] = useState(false);
  const [filterValues, setFilterValues] =
    useState<ClientEmployeeFilterFormValues>(getDefaultFilterValues());

  const filterFormRef = useRef<ClientEmployeeFilterFormRef>(null);

  const filtersActive = useMemo(() => {
    return areFiltersActive(filterValues);
  }, [filterValues]);

  const handleFilterClick = useCallback(() => {
    setIsFilterPanelOpen(true);
  }, []);

  const handleCloseFilterPanel = useCallback(() => {
    setIsFilterPanelOpen(false);
  }, []);

  const handleFiltersReset = useCallback(() => {
    setFilterValues(getDefaultFilterValues());
  }, []);

  const handleFilterChange = useCallback(
    (newValues: ClientEmployeeFilterFormValues) => {
      // Called when form is submitted (Enter key)
      setFilterValues(newValues);
      setIsFilterPanelOpen(false);
    },
    [],
  );

  const handleApplyFilters = useCallback(() => {
    // Get current values from the form and apply them
    if (filterFormRef.current) {
      const currentFormValues = filterFormRef.current.getCurrentValues();
      setFilterValues(currentFormValues);
      setIsFilterPanelOpen(false);
    }
  }, []);

  const handleColumnSettingsClick = useCallback(() => {
    setIsColumnSettingsOpen(true);
  }, []);

  const handleCloseColumnSettings = useCallback(() => {
    setIsColumnSettingsOpen(false);
  }, []);

  const { exportData, isExporting } = useClientEmployeesTableExport({
    tableId: ClientEmployeesTableConfig.tableId,
    clientId,
    exportType: "csv",
    nameSearch: filterValues.nameSearch,
    emailSearch: filterValues.emailSearch,
  });

  const handleExportToCsv = useCallback(async () => {
    try {
      await exportData();
      toast.success("Client employees exported successfully!");
    } catch (error) {
      toast.error("Failed to export client employees");
      console.error("Export error:", error);
    }
  }, [exportData]);

  return (
    <TableColumnSettingsProvider
      tableId={ClientEmployeesTableConfig.tableId}
      defaultColumnSettings={DEFAULT_CLIENT_EMPLOYEE_TABLE_COLUMN_SETTINGS}
      defaultColumnOrder={DEFAULT_CLIENT_EMPLOYEE_TABLE_COLUMN_ORDER}
    >
      <Card grow>
        <Card.Header withBottomPadding>
          <Card.Header.Title>User list</Card.Header.Title>
          <Card.Header.Actions>
            <Box direction="row" gap={8} align="center">
              {filtersActive && (
                <Text size={12} color={"textSecondary"}>
                  Filters are applied
                </Text>
              )}
              <IconButton
                size="sm"
                icon="faFilter"
                onClick={handleFilterClick}
                outline={"sm"}
                showBubble={filtersActive}
                tooltip="Show filters"
              />
              <IconButton
                size="sm"
                icon="faTableColumns"
                onClick={handleColumnSettingsClick}
                outline={"sm"}
                tooltip="Show/Hide columns"
              />
              <IconButton
                size="sm"
                icon={isExporting ? "faArrowsSpin" : "faFileCsv"}
                onClick={handleExportToCsv}
                outline={"sm"}
                tooltip={isExporting ? "Exporting..." : "Export table data to CSV"}
                disabled={isExporting}
              />
            </Box>
          </Card.Header.Actions>
        </Card.Header>
        <Card.Body noPadding grow>
          <ClientEmployeesTable
            clientId={clientId}
            initialPageIndex={initialPageIndex}
            showFooter={showFooter}
            showHeader={showHeader}
            showPageSizeDropdown={showPageSizeDropdown}
            pageSize={pageSize}
            nameSearch={filterValues.nameSearch}
            emailSearch={filterValues.emailSearch}
          />
        </Card.Body>
      </Card>

      <SlideInPanel
        isOpen={isFilterPanelOpen}
        onClose={handleCloseFilterPanel}
        onCancel={handleFiltersReset}
        onApply={handleApplyFilters}
        cancelButtonText={filtersActive ? "Clear filters" : undefined}
        applyButtonText="Apply filters"
        title="Employee Filters"
      >
        <ClientEmployeeFilterForm
          ref={filterFormRef}
          onFilterChange={handleFilterChange}
          values={filterValues}
          errorsAPI={undefined}
        />
      </SlideInPanel>

      <ClientEmployeesTableColumnCustomization
        isOpen={isColumnSettingsOpen}
        onClose={handleCloseColumnSettings}
      />
    </TableColumnSettingsProvider>
  );
};
